import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  Avatar,
  Paper,
  Fade,
  Skeleton,
  Stack,
  TablePagination,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  Add as AddIcon,
  MoreVert as MoreIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { selectUser } from "../../../store/slices/authSlice";
import { ROUTES } from "../../../constants";
import { leavesAPI } from "../../../services/api/leaves.api";
import { documentsAPI } from "../../../services/api/documents.api";
import useNotification from "../../../hooks/common/useNotification";

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "approved":
      return "success";
    case "pending":
      return "warning";
    case "approved by supervisor":
      return "info";
    case "approved by hr":
      return "primary";
    case "rejected":
      return "error";
    case "draft":
      return "default";
    default:
      return "default";
  }
};

const formatStatus = (status) => {
  if (!status) return "Unknown";
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const calculateDays = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  return diffDays;
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const LeaveList = () => {
  // ---------------------------------------------------------------------------
  // HOOKS & STATE
  // ---------------------------------------------------------------------------
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const { showSuccess, showError } = useNotification();

  // Data state
  const [leaves, setLeaves] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // UI state
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const selectedLeaveRef = useRef(null);

  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ---------------------------------------------------------------------------
  // COMPUTED VALUES
  // ---------------------------------------------------------------------------
  const tabLabels = [
    "All Applications",
    "Drafts",
    "Pending",
    "Approved",
    "Rejected",
  ];

  const getTabFilteredLeaves = (tabIndex) => {
    switch (tabIndex) {
      case 0: // All
        return leaves;
      case 1: // Drafts
        return leaves.filter((leave) => leave.approval_status === "draft");
      case 2: // Pending
        return leaves.filter((leave) =>
          ["pending", "approved by supervisor", "approved by hr"].includes(
            leave.approval_status
          )
        );
      case 3: // Approved
        return leaves.filter((leave) => leave.approval_status === "approved");
      case 4: // Rejected
        return leaves.filter((leave) => leave.approval_status === "rejected");
      default:
        return leaves;
    }
  };

  const tabCounts = tabLabels.map(
    (_, index) => getTabFilteredLeaves(index).length
  );

  const filteredLeaves = leaves.filter((leave) => {
    const matchesSearch =
      leave.leaveType?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leave.comment?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = !typeFilter || leave.type_id === parseInt(typeFilter);
    
    // Status filter: if statusFilter is set, use it; otherwise respect tab filter
    let matchesStatus = true;
    if (statusFilter) {
      // If status filter is explicitly set, use it (exact match)
      matchesStatus = leave.approval_status === statusFilter;
    } else {
      // If no status filter, respect the tab filter
      // Check if leave status matches the tab's expected statuses
      const tabFilteredStatuses = (() => {
        switch (activeTab) {
          case 0: // All
            return null; // No filter
          case 1: // Drafts
            return ["draft"];
          case 2: // Pending
            return ["pending", "approved by supervisor", "approved by hr"];
          case 3: // Approved
            return ["approved"];
          case 4: // Rejected
            return ["rejected"];
          default:
            return null;
        }
      })();
      
      if (tabFilteredStatuses === null) {
        matchesStatus = true; // All tab - show all
      } else {
        matchesStatus = tabFilteredStatuses.includes(leave.approval_status);
      }
    }

    return matchesSearch && matchesType && matchesStatus;
  });

  // Paginated leaves
  const paginatedLeaves = filteredLeaves.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    fetchLeaves();
    fetchLeaveTypes();
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [activeTab, searchTerm, typeFilter, statusFilter]);

  // ---------------------------------------------------------------------------
  // DATA FETCHING FUNCTIONS
  // ---------------------------------------------------------------------------
  const fetchLeaves = async () => {
    try {
      setLoading(true);
      // Backend automatically filters to show only user's own leaves
      const response = await leavesAPI.getAll();
      if (response && response.success) {
        setLeaves(response.data.applications || []);
      }
    } catch (error) {
      console.error("Error fetching leaves:", error);
      showError("Failed to load leave applications");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const response = await leavesAPI.getTypes();
      if (response && response.success) {
        setLeaveTypes(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching leave types:", error);
    }
  };

  // ---------------------------------------------------------------------------
  // EVENT HANDLERS
  // ---------------------------------------------------------------------------
  const handleRefresh = async () => {
    await fetchLeaves();
    showSuccess("Leave applications refreshed");
  };

  const handleMenuOpen = (event, leave) => {
    setAnchorEl(event.currentTarget);
    setSelectedLeave(leave);
    selectedLeaveRef.current = leave;
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    if (!deleteDialogOpen) {
      setSelectedLeave(null);
    }
  };

  const handleViewLeave = () => {
    if (selectedLeave) {
      navigate(`${ROUTES.LEAVES}/${selectedLeave.id}`);
    }
    handleMenuClose();
  };

  const handleEditLeave = () => {
    if (selectedLeave) {
      navigate(`${ROUTES.LEAVES}/${selectedLeave.id}/edit`);
    }
    handleMenuClose();
  };

  const handleDeleteLeave = () => {
    selectedLeaveRef.current = selectedLeave;
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDeleteLeave = async () => {
    const leaveToDelete = selectedLeaveRef.current;

    if (!leaveToDelete) {
      return;
    }

    try {
      setActionLoading(true);

      if (leaveToDelete.approval_status === "draft") {
        const response = await leavesAPI.delete(leaveToDelete.id);

        if (response && response.success) {
          showSuccess("Leave application deleted successfully");
          await fetchLeaves();
        } else {
          throw new Error(
            response?.message || "Failed to delete leave application"
          );
        }
      } else {
        showError("Only draft applications can be deleted");
      }
    } catch (error) {
      console.error("Error deleting leave:", error);
      showError(error.message || "Failed to delete leave application");
    } finally {
      setActionLoading(false);
      setDeleteDialogOpen(false);
      setSelectedLeave(null);
      selectedLeaveRef.current = null;
    }
  };

  const handleDownloadAttachment = async (leave) => {
    if (!leave.attachment || !leave.attachment.id) {
      showError("No attachment available for download");
      return;
    }

    try {
      const blob = await documentsAPI.download(leave.attachment.id);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = leave.attachment.name || "document";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showSuccess("Document downloaded successfully");
    } catch (error) {
      console.error("Download error:", error);
      if (error.message.includes("404")) {
        showError("Document not found or no longer available");
      } else {
        showError("Failed to download document");
      }
    }
  };

  // ---------------------------------------------------------------------------
  // PERMISSION CHECKS
  // ---------------------------------------------------------------------------
  const canEdit = (leave) => {
    // Allow editing draft or rejected leaves (rejected leaves can be edited and resubmitted)
    return leave.user_id === user.id && (leave.approval_status === "draft" || leave.approval_status === "rejected");
  };

  const canDelete = (leave) => {
    if (!leave || !user) return false;
    return leave.user_id === user.id && leave.approval_status === "draft";
  };

  // ---------------------------------------------------------------------------
  // RENDER FUNCTIONS
  // ---------------------------------------------------------------------------
  const renderStatusChip = (status) => (
    <Chip
      label={formatStatus(status)}
      color={getStatusColor(status)}
      size="small"
      sx={{
        fontWeight: 500,
        borderRadius: 1.5,
      }}
    />
  );

  const renderActionMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      PaperProps={{
        elevation: 3,
        sx: { minWidth: 180 },
      }}
    >
      <MenuItem onClick={handleViewLeave}>
        <ListItemIcon>
          <ViewIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>View Details</ListItemText>
      </MenuItem>

      {selectedLeave && canEdit(selectedLeave) && (
        <MenuItem onClick={handleEditLeave}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
      )}

      {selectedLeave &&
        selectedLeave.attachment &&
        selectedLeave.attachment.id && (
          <MenuItem onClick={() => handleDownloadAttachment(selectedLeave)}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download Attachment</ListItemText>
          </MenuItem>
        )}

      {selectedLeave && canDelete(selectedLeave) && (
        <MenuItem onClick={handleDeleteLeave} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Draft</ListItemText>
        </MenuItem>
      )}
    </Menu>
  );

  const renderLoadingSkeleton = () => (
    <>
      {Array.from({ length: 5 }).map((_, index) => (
        <TableRow key={index}>
          <TableCell>
            <Skeleton variant="text" width={150} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={200} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={80} />
          </TableCell>
          <TableCell>
            <Skeleton variant="rectangular" width={80} height={24} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={100} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={60} />
          </TableCell>
          <TableCell align="right">
            <Skeleton variant="circular" width={32} height={32} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );

  // ---------------------------------------------------------------------------
  // MAIN RENDER
  // ---------------------------------------------------------------------------
  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        bgcolor: "background.default",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            My Leave Applications
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage your leave requests
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={
              loading ? <CircularProgress size={16} /> : <RefreshIcon />
            }
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(ROUTES.LEAVE_CREATE)}
          >
            Apply for Leave
          </Button>
        </Stack>
      </Box>

      {/* Tabs */}
      <Fade in timeout={400}>
        <Card sx={{ mb: 3, borderRadius: 2 }} elevation={1}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                minHeight: 64,
                fontWeight: 500,
                fontSize: "0.95rem",
              },
              "& .Mui-selected": {
                color: "#37ABA6",
              },
            }}
          >
            {tabLabels.map((label, index) => (
              <Tab
                key={index}
                label={
                  <Box>
                    {label}
                    <Chip
                      label={tabCounts[index] || 0}
                      size="small"
                      sx={{
                        ml: 1,
                        height: 20,
                        minWidth: 20,
                        bgcolor:
                          activeTab === index ? "#37ABA6" : "action.hover",
                        color: activeTab === index ? "white" : "text.secondary",
                      }}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Card>
      </Fade>

      {/* Filters */}
      <Fade in timeout={500}>
        <Card sx={{ mb: 3, borderRadius: 2 }} elevation={1}>
          <CardContent>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <TextField
                placeholder="Search by leave type or comment..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1 }}
                size="small"
              />

              <TextField
                select
                label="Leave Type"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 180 } }}
                size="small"
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (selected === "" || !selected) {
                      return "All Types";
                    }
                    const selectedType = leaveTypes.find((type) => type.id === selected);
                    return selectedType ? selectedType.name : "All Types";
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <MenuItem value="">All Types</MenuItem>
                {leaveTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 180 } }}
                size="small"
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (selected === "" || !selected) {
                      return "All Status";
                    }
                    return formatStatus(selected);
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved by supervisor">Approved by Supervisor</MenuItem>
                <MenuItem value="approved by hr">Approved by HR</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  minWidth: "fit-content",
                }}
              >
                <FilterIcon fontSize="small" />
                <Typography variant="body2">
                  {filteredLeaves.length} of {leaves.length}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Fade>

      {/* Leave Table */}
      <Fade in timeout={600}>
        <Card sx={{ borderRadius: 2 }} elevation={1}>
          {filteredLeaves.length === 0 && !loading ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <CalendarIcon
                sx={{ fontSize: 60, color: "action.disabled", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No leave applications found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {searchTerm || typeFilter || statusFilter
                  ? "Try adjusting your search filters"
                  : activeTab === 0
                    ? "You haven't applied for any leave yet"
                    : `No ${tabLabels[activeTab].toLowerCase()} applications`}
              </Typography>
              {activeTab === 0 &&
                !searchTerm &&
                !typeFilter &&
                !statusFilter && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(ROUTES.LEAVE_CREATE)}
                  >
                    Apply for Your First Leave
                  </Button>
                )}
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell sx={{ fontWeight: 600 }}>Leave Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date Range</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Applied On</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Attachment</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading
                      ? renderLoadingSkeleton()
                      : paginatedLeaves.map((leave, index) => (
                          <TableRow
                            key={leave.id}
                            hover
                            sx={{
                              "&:hover": {
                                bgcolor: "action.hover",
                                cursor: "pointer",
                              },
                              transition: "background-color 0.2s",
                            }}
                            onClick={() =>
                              navigate(`${ROUTES.LEAVES}/${leave.id}`)
                            }
                          >
                            <TableCell>
                              <Box>
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  sx={{ mb: 0.5 }}
                                >
                                  {leave.leaveType?.name}
                                </Typography>
                                {leave.comment && (
                                  <Tooltip title={leave.comment}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      noWrap
                                      sx={{ maxWidth: 200, display: "block" }}
                                    >
                                      {leave.comment.length > 40
                                        ? `${leave.comment.substring(0, 40)}...`
                                        : leave.comment}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CalendarIcon
                                  fontSize="small"
                                  sx={{ color: "text.secondary" }}
                                />
                                <Typography variant="body2">
                                  {formatDate(leave.starting_date)}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  -
                                </Typography>
                                <Typography variant="body2">
                                  {formatDate(leave.end_date)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <TimeIcon
                                  fontSize="small"
                                  sx={{ color: "text.secondary" }}
                                />
                                <Typography variant="body2" fontWeight={500}>
                                  {calculateDays(
                                    leave.starting_date,
                                    leave.end_date
                                  )}{" "}
                                  {calculateDays(
                                    leave.starting_date,
                                    leave.end_date
                                  ) === 1
                                    ? "day"
                                    : "days"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {renderStatusChip(leave.approval_status)}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {formatDate(leave.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {leave.attachment ? (
                                <Tooltip title={leave.attachment.name}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadAttachment(leave);
                                    }}
                                    sx={{
                                      color: "#37ABA6",
                                      "&:hover": {
                                        bgcolor: "rgba(55, 171, 166, 0.08)",
                                      },
                                    }}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMenuOpen(e, leave);
                                }}
                                sx={{
                                  "&:hover": {
                                    bgcolor: "action.hover",
                                  },
                                }}
                              >
                                <MoreIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {!loading && filteredLeaves.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredLeaves.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </>
          )}
        </Card>
      </Fade>

      {/* Action Menu */}
      {renderActionMenu()}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !actionLoading && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Delete Leave Application
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this draft leave application? This
            action cannot be undone.
            {selectedLeave && (
              <Box
                sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}
              >
                <Typography variant="body2" color="text.primary" gutterBottom>
                  <strong>Leave Details:</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Type: {selectedLeave.leaveType?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Dates: {formatDate(selectedLeave.starting_date)} -{" "}
                  {formatDate(selectedLeave.end_date)}
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            loading={actionLoading}
            onClick={confirmDeleteLeave}
            startIcon={!actionLoading && <DeleteIcon />}
          >
            Delete Application
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveList;
